import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import {
  useRegisterFellowMutation,
  useRegisterProviderMutation,
  useValidateIDMutation,
} from "store/services/authAPI";
import { ReactComponent as IDIcon } from "images/svg/reg-id-icon.svg";
import { ReactComponent as NameIcon } from "images/svg/reg-user-name.svg";
import { ReactComponent as EnvIcon } from "images/svg/enveloope.svg";
import { ReactComponent as PadlockIcon } from "images/svg/padlock.svg";
import { ReactComponent as POCNameIcon } from "images/svg/poc-fullname.svg";
import { ReactComponent as OrgNameIcon } from "images/svg/org-name.svg";
import { capitalize, formatHyphen, replaceUnderscoresWithSpaces } from "utils/strings";
import { 
  LocalGov, 
  Nigerian_States, 
  OrganisationTypes 
} from "utils/constant.data";

import "./styles.css"
import { selectStyles } from "./selectStyles";
import Select, { components } from "react-select";


const RegisterCard = ({ mode, onNext, onBack }) => {
  const [isShown, setIsSHown] = useState(false);
  const [verify, setVerify] = useState({
    digitsValidated: false,
    digitsVerified: false,
  });
  const [LGAs, setLGAs] = useState([]);

  const [validateID, { isLoading: validatingID }] = useValidateIDMutation();
  const [registerFellow, { isLoading: registeringFellow }] = useRegisterFellowMutation();
  const [registerProvider, { isLoading: registeringProvider }] = useRegisterProviderMutation();

  const handleSubmit = async (values) => {
    console.log("Registration values:", values, "mode:", mode);
    
    if (mode === "alc") {
      // Handle ALC registration
      const { devs_in_gov_reg_code, alc_password, ...restValues } = values;
      const filteredValues = Object.keys(restValues).reduce((acc, key) => {
        if (
          restValues[key] !== null &&
          restValues[key] !== undefined &&
          restValues[key] !== ""
        ) {
          acc[key] = restValues[key];
        }
        return acc;
      }, {});
      // Add password to API body
      filteredValues.password = values.alc_password;
      try {
        await registerProvider(filteredValues).unwrap();
        
        if (onNext) onNext();
      } catch (error) {
        let errorTitle = "Error Occurred";
        let errorMessage = "An error occurred during registration";

        if (error.data && error.data.errors) {
          if (error.data.errors.non_field_errors) {
            errorMessage = error.data.errors.non_field_errors[0];
          } else {
            errorMessage = Object.values(error.data.errors).flat().join(", ");
          }
        } else if (error.data && error.data.detail) {
          errorMessage = error.data.detail;
        }

        if (error.data && error.data.code) {
          errorTitle = replaceUnderscoresWithSpaces(error.data.code);
        }

        Swal.fire({
          title: errorTitle,
          text: errorMessage,
          iconHtml:
            '<i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>',
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Try Again",
        });
      }
    } else {
      // Handle Fellow/Devs in Gov registration (original logic)
      const { devs_in_gov_reg_code, ...restValues } = values;
      const filteredValues = Object.keys(restValues).reduce((acc, key) => {
        if (
          restValues[key] !== null &&
          restValues[key] !== undefined &&
          restValues[key] !== ""
        ) {
          acc[key] = restValues[key];
        }
        return acc;
      }, {});

      if (mode === "devs_in_gov") {
        filteredValues.registered_via = "devs_in_gov";
      }

      try {
        await registerFellow(filteredValues).unwrap();
        
        if (onNext) onNext();
      } catch (error) {
        let errorTitle = "Error Occurred";
        let errorMessage = "An error occurred during registration";

        if (error.data && error.data.errors) {
          if (error.data.errors.non_field_errors) {
            errorMessage = error.data.errors.non_field_errors[0];
          } else {
            errorMessage = Object.values(error.data.errors).flat().join(", ");
          }
        } else if (error.data && error.data.detail) {
          errorMessage = error.data.detail;
        }

        if (error.data && error.data.code) {
          errorTitle = replaceUnderscoresWithSpaces(error.data.code);
        }

        Swal.fire({
          title: errorTitle,
          text: errorMessage,
          iconHtml:
            '<i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>',
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Try Again",
        });
      }
    }
  };

  const Control = ({ children, ...props }) => {
  return (
    <div className="">
      <components.Control {...props} className="">
        {children}
      </components.Control>
    </div>
  );
};

  const formik = useFormik({
    initialValues: {
      id_type: "",
      nin_bvn: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      devs_in_gov_reg_code: "",
      // ALC fields
      name: "",
      poc_fullname: "",
      poc_email: "",
      poc_phone: "",
      organisation_type: "",
      state: "",
      lga: "",
      alc_password: "",
    },
    validationSchema: RegisterSchema(mode),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    const handleInvalidID = (error) => {
      formik.setFieldValue("first_name", null);
      formik.setFieldValue("last_name", null);
      setVerify({
        digitsValidated: true,
        digitsVerified: false,
      });
      Swal.fire({
        title: "Error Occurred",
        text:
          error?.message ??
          `Invalid ${capitalize(formik.values?.id_type ?? "Value")}`,
        iconHtml: ` <i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>`,
        showCloseButton: true,
        customClass: {
          icon: "no-border",
        },
        confirmButtonText: "Try Again",
      });
    };

    const handleIDVerification = async () => {
      // Only run for fellow and devs_in_gov modes
      if ((mode === "fellow" || mode === "devs_in_gov") &&
          formik.values.nin_bvn && 
          formik.values.nin_bvn.length === 11 && 
          formik.values.id_type &&
          !verify.digitsValidated) {
        try {
          const data = await validateID({
            id_type: formik.values?.id_type,
            nin_bvn: formik.values?.nin_bvn,
          }).unwrap();

          const isValid = data?.data?.status ?? false;
          if (isValid) {
            const response = data?.data ?? null;
            setVerify({
              digitsValidated: true,
              digitsVerified: true,
            });
            formik.setFieldValue("first_name", response?.first_name);
            formik.setFieldValue("last_name", response?.last_name);
          } else {
            handleInvalidID({
              message: data?.data?.message,
            });
          }
        } catch (error) {
          handleInvalidID({
            message: "Error occured validating ID",
          });
        }
      }
    };

    // Only call handleIDVerification if nin_bvn is exactly 11 digits (all numbers)
    if (
      (mode === "fellow" || mode === "devs_in_gov") &&
      formik.values.nin_bvn &&
      formik.values.nin_bvn.length === 11 &&
      /^\d{11}$/.test(formik.values.nin_bvn)
    ) {
      handleIDVerification();
    }
    // eslint-disable-next-line
  }, [formik.values.id_type, formik.values.nin_bvn, mode, verify.digitsValidated]);

  const togglePassword = () => {
    setIsSHown((isShown) => !isShown);
  };

  const IDOptions = [
    { value: "nin", label: "NIN" },
    { value: "bvn", label: "BVN" },
  ];

  return (
    <>
      <div
        className="login-main-page auth-page"
        style={{
          backgroundSize: "contain",
        }}
      >
        <div className="login-wrapper w-100">
         
          <div className="container h-100">
            <div className={`row h-100 align-items-center justify-content-center ${validatingID ? "disabled" : ""}`}>
              <div className="col-xl-6 mt-3">
                <div className="card registration-card">
                  <div className="card-body p-0">
                    <div className="row m-0">
                      <div className="col-xl-12 col-md-12">
                        <div className="sign-in-your">
                          <div>
                            <h4 className="registration-heading">
                              Register to Join 3MTT
                            </h4>
                              <p className="registration-title">
                                {mode === "alc" 
                                  ? "Welcome aboard! Get ready to empower the next generation of talent." 
                                  : "Hurray, get ready to learn new skills"}
                              <span>
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M20.5603 9.23023C21.1174 10.1679 21.483 11.2067 21.6361 12.2865C21.7891 13.3664 21.7265 14.4659 21.4519 15.5214C21.1773 16.5769 20.6962 17.5675 20.0363 18.4358C19.3764 19.3042 18.5509 20.0331 17.6075 20.5804C16.6641 21.1276 15.6215 21.4824 14.5401 21.6242C13.4587 21.766 12.3599 21.6919 11.3074 21.4063C10.2548 21.1207 9.26925 20.6293 8.40782 19.9603C7.54639 19.2914 6.82614 18.4583 6.28873 17.5093L2.45155 10.8633C2.26902 10.5202 2.22684 10.1195 2.33389 9.74587C2.44094 9.3722 2.68886 9.05468 3.02542 8.86021C3.36197 8.66574 3.76088 8.60951 4.13808 8.70336C4.51527 8.79722 4.84131 9.03384 5.04748 9.36335L6.78748 12.3746C6.83673 12.4599 6.9023 12.5347 6.98045 12.5947C7.05859 12.6547 7.14779 12.6987 7.24294 12.7242C7.33809 12.7497 7.43733 12.7562 7.535 12.7433C7.63267 12.7305 7.72685 12.6985 7.81217 12.6493C7.89749 12.6 7.97227 12.5345 8.03225 12.4563C8.09223 12.3782 8.13623 12.289 8.16174 12.1938C8.18724 12.0987 8.19376 11.9994 8.18091 11.9018C8.16806 11.8041 8.13611 11.7099 8.08686 11.6246L4.40623 5.2496C4.22371 4.90642 4.18153 4.50579 4.28858 4.13212C4.39563 3.75845 4.64355 3.44093 4.98011 3.24646C5.31666 3.05199 5.71556 2.99576 6.09276 3.08961C6.46996 3.18347 6.796 3.42009 7.00217 3.7496L10.6875 10.1246C10.7353 10.2126 10.8002 10.2902 10.8785 10.3527C10.9567 10.4152 11.0467 10.4614 11.1431 10.4886C11.2395 10.5157 11.3403 10.5233 11.4397 10.5109C11.5391 10.4985 11.635 10.4662 11.7217 10.4161C11.8084 10.3661 11.8843 10.2991 11.9447 10.2192C12.0051 10.1394 12.0489 10.0482 12.0736 9.95111C12.0982 9.85404 12.1031 9.75301 12.0881 9.65399C12.073 9.55498 12.0383 9.45998 11.9859 9.3746L9.17342 4.4996C8.98033 4.15545 8.93063 3.74907 9.0351 3.36853C9.13957 2.98799 9.38978 2.66395 9.73153 2.46662C10.0733 2.2693 10.479 2.21459 10.8608 2.31436C11.2426 2.41412 11.5697 2.66032 11.7712 2.9996L15.5625 9.57335C14.7242 10.4182 14.2526 11.5594 14.25 12.7496C14.2472 13.7237 14.5633 14.672 15.15 15.4496C15.2085 15.5301 15.2824 15.5981 15.3675 15.6497C15.4525 15.7013 15.547 15.7355 15.6454 15.7503C15.7437 15.7651 15.8441 15.7601 15.9405 15.7357C16.037 15.7113 16.1276 15.668 16.2072 15.6082C16.2867 15.5484 16.3535 15.4735 16.4038 15.3876C16.4541 15.3018 16.4868 15.2068 16.5 15.1081C16.5132 15.0095 16.5067 14.9093 16.4807 14.8132C16.4548 14.7172 16.4101 14.6272 16.349 14.5487C16.1118 14.2319 15.9396 13.8714 15.8423 13.4878C15.745 13.1042 15.7246 12.7051 15.7822 12.3136C15.8398 11.9221 15.9743 11.5458 16.1779 11.2065C16.3816 10.8672 16.6503 10.5715 16.9687 10.3365C17.1168 10.2269 17.2194 10.0667 17.2569 9.88636C17.2944 9.70602 17.2643 9.51819 17.1722 9.35866L16.0978 7.4996C15.9047 7.15545 15.855 6.74907 15.9595 6.36853C16.0639 5.98799 16.3142 5.66395 16.6559 5.46662C16.9976 5.2693 17.4034 5.21459 17.7852 5.31436C18.167 5.41412 18.4941 5.66032 18.6956 5.9996L20.5603 9.23023ZM17.8069 3.88648C18.3324 4.02495 18.8251 4.26649 19.2564 4.59707C19.6877 4.92765 20.0491 5.34066 20.3194 5.8121L20.3503 5.86554C20.4498 6.03785 20.6136 6.16359 20.8058 6.2151C20.9009 6.24061 21.0001 6.24712 21.0978 6.23428C21.1955 6.22143 21.2897 6.18947 21.375 6.14023C21.4603 6.09098 21.5351 6.02541 21.5951 5.94726C21.655 5.86912 21.699 5.77992 21.7245 5.68477C21.7501 5.58962 21.7566 5.49038 21.7437 5.39271C21.7309 5.29504 21.6989 5.20086 21.6497 5.11554L21.6187 5.0621C21.2501 4.41942 20.7575 3.8564 20.1694 3.40576C19.5813 2.95513 18.9095 2.62586 18.1931 2.4371C18.0015 2.38754 17.798 2.41576 17.6271 2.5156C17.4561 2.61544 17.3316 2.77881 17.2806 2.97009C17.2297 3.16136 17.2564 3.36503 17.3549 3.5367C17.4535 3.70836 17.616 3.83409 17.8069 3.88648ZM7.46061 21.1609C6.48876 20.3939 5.67254 19.4481 5.05592 18.3746C5.00668 18.2893 4.94111 18.2145 4.86296 18.1545C4.78481 18.0945 4.69562 18.0505 4.60047 18.025C4.50532 17.9995 4.40607 17.993 4.3084 18.0059C4.21074 18.0187 4.11655 18.0507 4.03123 18.0999C3.94592 18.1492 3.87113 18.2147 3.81116 18.2929C3.75118 18.371 3.70718 18.4602 3.68167 18.5554C3.65616 18.6505 3.64965 18.7498 3.6625 18.8474C3.67534 18.9451 3.7073 19.0393 3.75655 19.1246C4.46834 20.3629 5.41024 21.4537 6.53155 22.3384C6.68794 22.4587 6.88545 22.5125 7.08127 22.4882C7.27709 22.4639 7.45544 22.3634 7.57767 22.2085C7.6999 22.0536 7.75615 21.8567 7.73424 21.6606C7.71233 21.4645 7.61401 21.285 7.46061 21.1609Z"
                                    fill="#010D14"
                                  />
                                </svg>
                              </span>
                            </p>
                          </div>

                          <form className="form mt-4 apply-form" onSubmit={formik.handleSubmit}>
                            {/* ALC Mode - Organization Fields */}
                            {mode === "alc" && (
                              <>
                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>Organisation Name</strong>
                                  </label>
                                  <div className="password__container">
                                    <OrgNameIcon />
                                    <input
                                      type="text"
                                      id="name"
                                      name="name"
                                      className="form-control"
                                      placeholder="Organisation name"
                                      {...formik.getFieldProps("name")}
                                    />
                                  </div>
                                  {formik.touched.name && formik.errors.name ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.name}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>POC Full Name</strong>
                                  </label>
                                  <div className="password__container">
                                    <POCNameIcon />
                                    <input
                                      type="text"
                                      id="poc_fullname"
                                      name="poc_fullname"
                                      className="form-control"
                                      placeholder="POC full name"
                                      {...formik.getFieldProps("poc_fullname")}
                                    />
                                  </div>
                                  {formik.touched.poc_fullname && formik.errors.poc_fullname ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.poc_fullname}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>POC Email</strong>
                                  </label>
                                  <div className="password__container">
                                    <EnvIcon />
                                    <input
                                      type="email"
                                      id="poc_email"
                                      name="poc_email"
                                      className="form-control"
                                      placeholder="e.g. example@domainname.com"
                                      {...formik.getFieldProps("poc_email")}
                                    />
                                  </div>
                                  {formik.touched.poc_email && formik.errors.poc_email ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.poc_email}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>POC Phone</strong>
                                  </label>
                                  <div className="password__container">
                                    <IDIcon />
                                    <input
                                      type="text"
                                      id="poc_phone"
                                      name="poc_phone"
                                      className="form-control"
                                      placeholder="POC phone"
                                      {...formik.getFieldProps("poc_phone")}
                                    />
                                  </div>
                                  {formik.touched.poc_phone && formik.errors.poc_phone ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.poc_phone}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>Organisation Type</strong>
                                  </label>
                                  <Select
                                    value={OrganisationTypes.map(type => ({
                                      value: type,
                                      label: formatHyphen(type)
                                    })).find(option => option.value === formik.values.organisation_type)}
                                    onChange={(selectedOption) => {
                                      formik.setFieldValue("organisation_type", selectedOption?.value || "");
                                    }}
                                    options={OrganisationTypes.map(type => ({
                                      value: type,
                                      label: formatHyphen(type)
                                    }))}
                                    styles={selectStyles}
                                    components={{ Control }}


                                    placeholder="Select Organisation Type"
                                  />
                                  {formik.touched.organisation_type && formik.errors.organisation_type ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.organisation_type}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>State of training centre</strong>
                                  </label>
                                  <Select
                                    value={Nigerian_States.map(state => ({
                                      value: state,
                                      label: state
                                    })).find(option => option.value === formik.values.state)}
                                    onChange={(selectedOption) => {
                                      const selectedState = selectedOption?.value || "";
                                      formik.setFieldValue("state", selectedState);
                                      formik.setFieldTouched("state", true);

                                      const _selectedLGAs =
                                        LocalGov.find(
                                          (val) =>
                                            val?.state?.toLowerCase() ===
                                            selectedState.toLowerCase()
                                        )?.lgas || [];

                                      setLGAs([..._selectedLGAs]);
                                      formik.setFieldValue("lga", "");
                                    }}
                                    options={Nigerian_States.map(state => ({
                                      value: state,
                                      label: state
                                    }))}
                                    styles={selectStyles}
                                    components={{ Control }}
                                    placeholder="Select State"
                                  />
                                  {formik.touched.state && formik.errors.state ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.state}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>Local Government</strong>
                                  </label>
                                  <Select
                                    value={LGAs.map(lga => ({
                                      value: lga,
                                      label: lga
                                    })).find(option => option.value === formik.values.lga)}
                                    onChange={(selectedOption) => {
                                      formik.setFieldValue("lga", selectedOption?.value || "");
                                    }}
                                    options={LGAs.map(lga => ({
                                      value: lga,
                                      label: lga
                                    }))}
                                    styles={selectStyles}
                                    components={{ Control }}
                                    placeholder="Select Local Government"
                                    isDisabled={!formik.values.state}
                                  />
                                  {formik.touched.lga && formik.errors.lga ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.lga}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>Password</strong>
                                  </label>
                                  <div className="password__container">
                                    <PadlockIcon />
                                    <input
                                      type={isShown ? "text" : "password"}
                                      id="alc_password"
                                      name="alc_password"
                                      className="form-control"
                                      placeholder="Enter Password"
                                      {...formik.getFieldProps("alc_password")}
                                    />
                                    <button type="button" onClick={togglePassword}>
                                      <div className="icon">
                                        {isShown ? (
                                          <i className="bi bi-eye"></i>
                                        ) : (
                                          <i className="bi bi-eye-slash"></i>
                                        )}
                                      </div>
                                    </button>
                                  </div>
                                  {formik.touched.alc_password && formik.errors.alc_password ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.alc_password}
                                    </div>
                                  ) : null}
                                </div>
                              </>
                            )}

                            {/* Fellow and Devs in Gov Mode */}
                            {(mode === "fellow" || mode === "devs_in_gov") && (
                              <>
                                {/* Registration Code for Devs in Gov */}
                                {mode === "devs_in_gov" && (
                                  <div className="mb-3">
                                    <label className="mb-1">
                                      <strong>Registration Code</strong>
                                    </label>
                                    <div className="password__container">
                                      <IDIcon />
                                      <input
                                        type="text"
                                        id="devs_in_gov_reg_code"
                                        name="devs_in_gov_reg_code"
                                        className="form-control"
                                        placeholder="Enter 6-character registration code"
                                        {...formik.getFieldProps("devs_in_gov_reg_code")}
                                      />
                                    </div>
                                    {formik.touched.devs_in_gov_reg_code && formik.errors.devs_in_gov_reg_code ? (
                                      <div className="text-danger mt-2 fs-12">
                                        {formik.errors.devs_in_gov_reg_code}
                                      </div>
                                    ) : null}
                                  </div>
                                )}

                                {/* ID Type Selection */}
                                <div className="mb-3">
                                  <label className="mb-1">
                                    <strong>Select a means of Identification</strong>
                                  </label>
                                  <Select
                                    value={IDOptions.find(option => option.value === formik.values.id_type)}
                                    onChange={(selectedOption) => {
                                      formik.setFieldValue("id_type", selectedOption?.value || "");
                                      setVerify({
                                        digitsValidated: false,
                                        digitsVerified: false,
                                      });
                                      formik.setFieldValue("nin_bvn", "");
                                      formik.setFieldValue("first_name", "");
                                      formik.setFieldValue("last_name", "");
                                    }}
                                    options={IDOptions}
                                    styles={selectStyles}
                                    components={{ Control }}
                                    placeholder="Select ID Type"
                                  />
                                  {formik.touched.id_type && formik.errors.id_type ? (
                                    <div className="text-danger mt-2 fs-12">
                                      {formik.errors.id_type}
                                    </div>
                                  ) : null}
                                </div>

                                {/* ID Number Input */}
                                {(formik.values.id_type === "nin" || formik.values.id_type === "bvn") && (
                                  <div className="mb-3">
                                    <label>
                                      <strong>Enter your {formik.values.id_type?.toUpperCase()}</strong>
                                    </label>
                                    <div className="password__container">
                                      <IDIcon />
                                      <input
                                        id="nin_bvn"
                                        name="nin_bvn"
                                        className="form-control"
                                        placeholder={`Enter ${formik.values.id_type?.toUpperCase()}`}
                                        {...formik.getFieldProps("nin_bvn")}
                                        onChange={(e) => {
                                          if (verify.digitsValidated) {
                                            setVerify({
                                              digitsValidated: false,
                                              digitsVerified: false,
                                            });
                                            formik.setFieldValue("first_name", "");
                                            formik.setFieldValue("last_name", "");
                                          }
                                          formik.handleChange(e);
                                        }}
                                      />
                                    </div>
                                    {formik.touched.nin_bvn && formik.errors.nin_bvn ? (
                                      <div className="text-danger mt-2 fs-12">
                                        {formik.errors.nin_bvn}
                                      </div>
                                    ) : null}
                                  </div>
                                )}

                                {/* Validating ID Loading */}
                                {validatingID && (
                                  <div className="mb-3">
                                    <label className="mb-1">
                                      <span
                                        role="status"
                                        aria-hidden="true"
                                        className="spinner-border spinner-border-sm ms-2"
                                      ></span>{" "}
                                      Validating {capitalize(formik.values.id_type)} .........
                                    </label>
                                  </div>
                                )}

                                {/* Verified Name Display and Additional Fields */}
                                {verify?.digitsVerified && (
                                  <>
                                    <div className="name-row">
                                      <NameIcon className="" />
                                      <h6>
                                        {capitalize(formik.values.first_name)} {capitalize(formik.values.last_name)}
                                      </h6>
                                    </div>

                                    <div className="form-group mb-3">
                                      <label>
                                        <strong>Email Address</strong>
                                      </label>
                                      <div className="password__container">
                                        <EnvIcon />
                                        <input
                                          type="email"
                                          id="email"
                                          name="email"
                                          className="form-control"
                                          placeholder="E.g. ade@gmail.com"
                                          {...formik.getFieldProps("email")}
                                        />
                                      </div>
                                      {formik.touched.email && formik.errors.email ? (
                                        <div className="text-danger mt-2 fs-12">
                                          {formik.errors.email}
                                        </div>
                                      ) : null}
                                    </div>

                                    <div className="form-group mb-3">
                                      <label>
                                        <strong>Create a Password</strong>
                                      </label>
                                      <div className="password__container">
                                        <PadlockIcon />
                                        <input
                                          type={isShown ? "text" : "password"}
                                          id="password"
                                          name="password"
                                          className="form-control"
                                          placeholder="Enter Password"
                                          {...formik.getFieldProps("password")}
                                        />
                                        <button type="button" onClick={togglePassword}>
                                          <div className="icon">
                                            {isShown ? (
                                              <i className="bi bi-eye"></i>
                                            ) : (
                                              <i className="bi bi-eye-slash"></i>
                                            )}
                                          </div>
                                        </button>
                                      </div>
                                      {formik.touched.password && formik.errors.password ? (
                                        <div className="text-danger mt-2 fs-12">
                                          {formik.errors.password}
                                        </div>
                                      ) : null}
                                    </div>

                                    <div className="text-center">
                                      <button
                                        type="submit"
                                        className="btn btn-primary btn-block"
                                        disabled={!formik.isValid || !formik.dirty || registeringFellow}
                                      >
                                        {registeringFellow ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Verifying...
                                          </>
                                        ) : (
                                          "Verify Account"
                                        )}
                                      </button>
                                    </div>

                                    
                                  </>
                                )}
                              </>
                            )}

                            {/* Submit Button for ALC */}
                            {mode === "alc" && (
                              <>
                                <div className="text-center">
                                  <button
                                    type="submit"
                                    className="btn btn-primary btn-block"
                                    disabled={
                                      registeringProvider ||
                                      !formik.isValid ||
                                      !formik.dirty
                                    }
                                  >
                                    {registeringProvider ? "Submitting..." : "Register"}
                                  </button>
                                </div>

                              </>
                            )}
                          </form>

                          
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const RegisterSchema = (mode) => {
  const baseSchema = {
    // Fellow and devs_in_gov fields
    ...(mode === "fellow" || mode === "devs_in_gov" ? {
      id_type: Yup.string().required("Select ID Type"),
      nin_bvn: Yup.string()
        .required("ID number is required")
        .min(8, "ID number must be at least 8 characters")
        .max(20, "ID number can't be greater than 20 characters"),
      first_name: Yup.string().required("First name is required"),
      last_name: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
      // confirmPassword: Yup.string()
      //   .required("Please confirm your password")
      //   .oneOf([Yup.ref("password"), null], "Passwords must match"),
    } : {}),

    // Devs in gov specific field
    ...(mode === "devs_in_gov" ? {
      devs_in_gov_reg_code: Yup.string()
        .required("Registration code is required")
        .length(6, "Registration code must be exactly 6 characters")
        .test(
          'matches-env-code', 
          'Invalid registration code', 
          function(value) {
            return value === "DEHEVD";
          }
        ),
    } : {}),

    // ALC fields
    ...(mode === "alc" ? {
      name: Yup.string().required("Organisation name is required"),
      poc_fullname: Yup.string().required("POC full name is required"),
      poc_email: Yup.string().email("Invalid email").required("POC email is required"),
      poc_phone: Yup.string().required("POC phone is required"),
      organisation_type: Yup.string().required("Organisation type is required"),
      state: Yup.string().required("State of training centre is required"),
      lga: Yup.string().required("LGA of training centre is required"),
      alc_password: Yup.string().required("Password is required").min(6, "Password must be at least 6 characters"),
    } : {}),
  };

  return Yup.object().shape(baseSchema);
};

export default RegisterCard;